import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError, inr, fmtDate, fmtDateTime } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Plus, Check, Clock, XCircle, ClipboardText, Trash, Warning, PencilSimple,
  MicrosoftExcelLogo, Printer, WhatsappLogo, EnvelopeSimple,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { downloadCSV } from "@/lib/reportExport";
import { openPrintStatement } from "@/lib/printStatement";
import BulkImportButton from "@/components/BulkImportButton";
import SearchableCustomerSelect from "@/components/SearchableCustomerSelect";
import SearchableItemSelect from "@/components/SearchableItemSelect";
import { buildPendingWorksPdf } from "@/lib/pendingWorksPdf";

/** Big centered warning graphic used inside the overdue alert modal. */
const OverdueGraphic = () => (
  <svg viewBox="0 0 240 200" width="220" height="180" className="mx-auto" aria-hidden>
    <defs>
      <radialGradient id="gRad" cx="50%" cy="50%" r="60%">
        <stop offset="0%" stopColor="hsl(var(--destructive))" stopOpacity="0.30" />
        <stop offset="60%" stopColor="hsl(var(--destructive))" stopOpacity="0.06" />
        <stop offset="100%" stopColor="hsl(var(--destructive))" stopOpacity="0" />
      </radialGradient>
      <linearGradient id="clockG" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#fff" stopOpacity="1" />
        <stop offset="100%" stopColor="#fef2f2" stopOpacity="1" />
      </linearGradient>
    </defs>
    <circle cx="120" cy="100" r="90" fill="url(#gRad)">
      <animate attributeName="r" values="88;96;88" dur="2.4s" repeatCount="indefinite" />
    </circle>
    {/* Clock body */}
    <circle cx="120" cy="100" r="58" fill="url(#clockG)" stroke="hsl(var(--destructive))" strokeWidth="4" />
    {/* Clock ticks */}
    {[0, 30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330].map((deg) => {
      const rad = (deg * Math.PI) / 180;
      const x1 = 120 + Math.cos(rad) * 48;
      const y1 = 100 + Math.sin(rad) * 48;
      const x2 = 120 + Math.cos(rad) * 54;
      const y2 = 100 + Math.sin(rad) * 54;
      return <line key={deg} x1={x1} y1={y1} x2={x2} y2={y2} stroke="hsl(var(--destructive))" strokeWidth="2" opacity="0.6" />;
    })}
    {/* Hour hand — dramatic angle past deadline */}
    <line x1="120" y1="100" x2="150" y2="72" stroke="hsl(var(--destructive))" strokeWidth="5" strokeLinecap="round">
      <animateTransform attributeName="transform" type="rotate" from="0 120 100" to="20 120 100" dur="0.6s" fill="freeze" />
    </line>
    {/* Minute hand */}
    <line x1="120" y1="100" x2="120" y2="52" stroke="#0f172a" strokeWidth="4" strokeLinecap="round">
      <animateTransform attributeName="transform" type="rotate" from="0 120 100" values="0;-8;0" dur="1.2s" repeatCount="indefinite" />
    </line>
    <circle cx="120" cy="100" r="6" fill="hsl(var(--destructive))" />
    {/* Alert badge */}
    <g transform="translate(170, 40)">
      <circle r="24" fill="hsl(var(--destructive))">
        <animate attributeName="r" values="22;26;22" dur="1.2s" repeatCount="indefinite" />
      </circle>
      <text x="0" y="7" textAnchor="middle" fill="#fff" fontSize="30" fontWeight="900">!</text>
    </g>
  </svg>
);

import { Dialog as OverdueDialog, DialogContent as OverdueDialogContent } from "@/components/ui/dialog";

const today = () => new Date().toISOString().slice(0, 10);
const nowLocal = () => {
  // Format for datetime-local: YYYY-MM-DDTHH:MM (local, no tz)
  const d = new Date();
  d.setSeconds(0, 0);
  const off = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - off).toISOString().slice(0, 16);
};
const inOneDay = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setSeconds(0, 0);
  const off = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - off).toISOString().slice(0, 16);
};
const fmtDeadline = (s) => fmtDateTime(s);

function emptyTender() {
  return {
    tender_id: "",
    name_of_work: "",
    value: 0,
    fees: 0,
    emd: 0,
    publish_date: "",
    last_date: "",
    organisation_chain: "",
    reference_number: "",
    bid_opening_date: "",
    bid_opening_place: "",
  };
}

function TenderSection({ f, setF }) {
  const [scrapingUrl, setScrapingUrl] = useState(-1);          // index being URL-scraped, -1 = none
  const [urlBox, setUrlBox] = useState({});                     // {[index]: pastedUrl}

  // Seed a first tender when the operator turns the toggle on (or when we're
  // editing a legacy record that has flat tender_* fields but no `tenders[]`).
  useEffect(() => {
    if (!f.is_tender) return;
    if ((f.tenders || []).length > 0) return;
    const seed = {
      ...emptyTender(),
      tender_id: f.tender_id || "",
      name_of_work: f.tender_name_of_work || "",
      value: Number(f.tender_value) || 0,
      fees: Number(f.tender_fees) || 0,
      emd: Number(f.tender_emd) || 0,
      publish_date: f.tender_publish_date || "",
      last_date: f.tender_last_date || "",
      organisation_chain: f.tender_organisation_chain || "",
      reference_number: f.tender_reference_number || "",
      bid_opening_date: f.tender_bid_opening_date || "",
      bid_opening_place: f.tender_bid_opening_place || "",
    };
    setF((prev) => ({ ...prev, tenders: [seed] }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f.is_tender]);

  const tenders = f.tenders || [];
  const setTender = (i, patch) => {
    // Guard: within this row, the same Tender ID cannot repeat across cards.
    if (Object.prototype.hasOwnProperty.call(patch, "tender_id")) {
      const nextId = String(patch.tender_id || "").trim().toUpperCase();
      if (nextId) {
        const dupe = (f.tenders || []).some(
          (t, idx) => idx !== i && String(t.tender_id || "").trim().toUpperCase() === nextId,
        );
        if (dupe) {
          toast.error(`Tender ID "${patch.tender_id}" is already added in this pending work`);
          return;
        }
      }
    }
    setF((prev) => ({
      ...prev,
      tenders: (prev.tenders || []).map((t, idx) => (idx === i ? { ...t, ...patch } : t)),
    }));
  };
  const addTender = () => setF((prev) => {
    const nextTenders = [...(prev.tenders || []), emptyTender()];
    // Going from 1 → 2+ tenders: stop auto-mirroring. Reset Deadline to
    // the current date-time as a fresh baseline for the operator to edit.
    if (nextTenders.length >= 2) {
      toast.info("Multiple tenders — please select the Deadline date & time manually", { duration: 4500 });
    }
    return {
      ...prev,
      tenders: nextTenders,
      deadline_tender_index: null,
      ...(nextTenders.length >= 2 ? { deadline: nowLocal() } : {}),
    };
  });
  const removeTender = (i) => setF((prev) => ({
    ...prev,
    tenders: (prev.tenders || []).length > 1 ? (prev.tenders || []).filter((_, idx) => idx !== i) : prev.tenders,
  }));

  // Mirror a human-readable summary of ALL tenders into the Description
  // textarea — only when it's empty or already tender-generated.
  useEffect(() => {
    if (!f.is_tender || !tenders.length) return;
    const blocks = tenders.map((t, idx) => {
      const parts = [];
      if (t.tender_id) parts.push(`Tender ID: ${t.tender_id}`);
      if (t.name_of_work) parts.push(`Name of Work: ${t.name_of_work}`);
      if (Number(t.value) > 0) parts.push(`Tender Value: ${inr(Number(t.value))}`);
      if (t.last_date) parts.push(`Last Date of Tender: ${fmtDateTime(t.last_date)}`);
      if (!parts.length) return "";
      return tenders.length > 1 ? `Tender ${idx + 1}\n${parts.join("\n")}` : parts.join("\n");
    }).filter(Boolean);
    if (!blocks.length) return;
    const summary = blocks.join("\n\n");
    const current = (f.description || "").trim();
    const isOwn = current === "" || current.startsWith("Tender ID:") || current.startsWith("Tender 1");
    if (isOwn && current !== summary) {
      setF((prev) => ({ ...prev, description: summary }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f.is_tender, JSON.stringify(tenders.map((t) => [t.tender_id, t.name_of_work, t.value, t.last_date]))]);

  // Mirror the tender's "Last Date" into the pending-work Deadline.
  // • 1 tender  → auto-mirror (index = 0)
  // • 2+ tenders → operator must pick which tender's Last Date is the
  //                deadline via `deadline_tender_index`. Never auto-mirror
  //                until they choose.
  useEffect(() => {
    if (!f.is_tender) return;
    const list = f.tenders || [];
    if (list.length === 0) return;
    if (list.length === 1) {
      // Single tender — auto-mirror its Last Date to the pending-work Deadline.
      const ld = (list[0].last_date || "").slice(0, 16);
      setF((prev) => ({
        ...prev,
        deadline_tender_index: 0,
        ...(ld ? { deadline: ld } : {}),
      }));
      return;
    }
    // 2+ tenders — do NOT auto-mirror. The operator picks the Deadline manually.
    if (f.deadline_tender_index != null) {
      setF((prev) => ({ ...prev, deadline_tender_index: null }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    f.is_tender,
    (f.tenders || []).length,
    JSON.stringify((f.tenders || []).map((t) => t.last_date || "")),
  ]);

  const applyFetched = (i, data) => {
    const incomingId = String(data.tender_id || "").trim().toUpperCase();
    if (incomingId) {
      const dupe = (f.tenders || []).some(
        (t, idx) => idx !== i && String(t.tender_id || "").trim().toUpperCase() === incomingId,
      );
      if (dupe) {
        toast.error(`Tender ID "${data.tender_id}" is already added in this pending work`);
        return;
      }
    }
    setTender(i, {
      tender_id: data.tender_id || tenders[i]?.tender_id || "",
      name_of_work: data.name_of_work || tenders[i]?.name_of_work || "",
      value: Number(data.tender_value) || tenders[i]?.value || 0,
      fees: Number(data.tender_fees) || tenders[i]?.fees || 0,
      emd: Number(data.tender_emd) || tenders[i]?.emd || 0,
      publish_date: data.publish_date || tenders[i]?.publish_date || "",
      last_date: data.last_date || tenders[i]?.last_date || "",
      organisation_chain: data.organisation || tenders[i]?.organisation_chain || "",
      reference_number: data.reference_number || tenders[i]?.reference_number || "",
      bid_opening_date: data.bid_opening_date || tenders[i]?.bid_opening_date || "",
      bid_opening_place: data.bid_opening_place || tenders[i]?.bid_opening_place || "",
    });
    const filled = [
      data.name_of_work && "Name",
      Number(data.tender_value) && "Value",
      Number(data.tender_fees) && "Fees",
      Number(data.tender_emd) && "EMD",
      data.publish_date && "Publish Date",
      data.last_date && "Last Date",
      data.organisation && "Org Chain",
      data.reference_number && "Ref No",
      data.bid_opening_date && "Bid Opening",
      data.bid_opening_place && "Opening Place",
    ].filter(Boolean);
    toast.success(filled.length ? `Tender ${i + 1} — Fetched: ${filled.join(", ")}` : `Tender ${i + 1} — details couldn't be parsed, fill manually`);
  };

  const fetchByUrl = async (i) => {
    const url = (urlBox[i] || "").trim();
    if (!url) { toast.error("Paste the tender URL from MahaTenders first"); return; }
    if (!url.startsWith("https://mahatenders.gov.in/")) {
      toast.error("URL must be from mahatenders.gov.in"); return;
    }
    setScrapingUrl(i);
    const t = toast.loading(`Fetching Tender ${i + 1} from URL…`, { duration: 30000 });
    try {
      const { data } = await api.post("/tenders/scrape-url", { tender_detail_url: url });
      toast.dismiss(t);
      applyFetched(i, data);
      setUrlBox((prev) => ({ ...prev, [i]: "" }));
    } catch (err) {
      toast.dismiss(t); toast.error(formatApiError(err));
    } finally { setScrapingUrl(-1); }
  };

  const openOnPortal = () => window.open(
    "https://mahatenders.gov.in/nicgep/app",
    "_blank", "noopener"
  );

  return (
    <div className="rounded-lg border border-border">
      <label className="flex items-center gap-2 px-4 py-3 border-b border-border/70 cursor-pointer">
        <input
          type="checkbox"
          checked={!!f.is_tender}
          onChange={(e) => setF((prev) => ({ ...prev, is_tender: e.target.checked }))}
          className="h-4 w-4 accent-primary"
          data-testid="pw-is-tender-toggle"
        />
        <span className="eyebrow" style={{ color: "hsl(var(--primary))" }}>Government Tender</span>
        <span className="text-[11.5px] text-muted-foreground ml-1">Track one or more tenders inside this pending work</span>
      </label>

      {f.is_tender && (
        <div className="p-4 space-y-4">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="text-[12.5px] text-muted-foreground">
              {tenders.length} tender{tenders.length === 1 ? "" : "s"} in this pending work.
              Paste the MahaTenders URL on any card to auto-fill in ~5s.
            </div>
            <div className="flex items-center gap-2">
              <Button type="button" size="sm" variant="outline" onClick={openOnPortal} data-testid="pw-tender-portal">
                Open MahaTenders →
              </Button>
              <Button type="button" size="sm" onClick={addTender} data-testid="pw-tender-add">
                <Plus size={13} className="mr-1" /> Add another tender
              </Button>
            </div>
          </div>

          {tenders.length === 1 && (
            <div className="rounded-md border border-primary/30 bg-primary/[.05] px-3 py-2 text-[11.5px] text-primary/90"
                 data-testid="pw-deadline-mirror-single">
              Deadline auto-mirrors from this tender's <b>Last Date</b>.
            </div>
          )}
          {tenders.length >= 2 && (
            <div className="rounded-md border border-amber-400/70 bg-amber-50 dark:bg-amber-500/5 px-3 py-2.5 text-[12px] text-amber-900 dark:text-amber-200 flex items-start gap-2"
                 data-testid="pw-deadline-mirror-multi">
              <span className="text-base leading-none">⚠️</span>
              <span>
                <b>Please select the Deadline date & time manually.</b>
                <span className="block text-[11px] mt-0.5 opacity-90">
                  Auto-mirror is OFF because this pending work has more than one tender.
                </span>
              </span>
            </div>
          )}

          {tenders.map((t, i) => (
            <div key={i} className="rounded-md border border-primary/30 bg-primary/[.04] p-3 space-y-3" data-testid={`pw-tender-card-${i}`}>
              <div className="flex items-center justify-between">
                <div className="eyebrow" style={{ color: "hsl(var(--primary))" }}>Tender {i + 1}</div>
                {tenders.length > 1 && (
                  <Button type="button" variant="ghost" size="icon" className="h-7 w-7 text-destructive"
                    onClick={() => removeTender(i)} data-testid={`pw-tender-remove-${i}`}>
                    <Trash size={13} />
                  </Button>
                )}
              </div>

              <div className="flex items-stretch gap-2">
                <Input
                  value={urlBox[i] || ""}
                  onChange={(e) => setUrlBox((prev) => ({ ...prev, [i]: e.target.value }))}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); fetchByUrl(i); } }}
                  placeholder="Paste MahaTenders tender detail URL → auto-fills"
                  className="font-mono flex-1"
                  data-testid={`pw-tender-${i}-url-paste`}
                />
                <Button type="button" onClick={() => fetchByUrl(i)} disabled={scrapingUrl === i}
                  data-testid={`pw-tender-${i}-url-fetch`}>
                  {scrapingUrl === i ? "Fetching…" : "Fetch"}
                </Button>
              </div>

              <div className="space-y-1.5">
                <Label className="eyebrow">Tender ID</Label>
                <Input value={t.tender_id}
                  onChange={(e) => setTender(i, { tender_id: e.target.value.trim() })}
                  placeholder="e.g. 2026_MEDA_1177089_1"
                  className="font-mono" data-testid={`pw-tender-${i}-id`} />
              </div>

              <div className="space-y-1.5">
                <Label className="eyebrow">Name of Work</Label>
                <Textarea rows={2} value={t.name_of_work}
                  onChange={(e) => setTender(i, { name_of_work: e.target.value })}
                  placeholder="Auto-filled after Fetch, or type manually"
                  data-testid={`pw-tender-${i}-name`} />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="space-y-1.5">
                  <Label className="eyebrow">Tender Value (₹)</Label>
                  <Input type="number" min={0} step="0.01" value={t.value}
                    onChange={(e) => setTender(i, { value: Number(e.target.value) || 0 })}
                    className="font-mono text-right tabular-nums" data-testid={`pw-tender-${i}-value`} />
                </div>
                <div className="space-y-1.5">
                  <Label className="eyebrow">Tender Fees (₹)</Label>
                  <Input type="number" min={0} step="0.01" value={t.fees}
                    onChange={(e) => setTender(i, { fees: Number(e.target.value) || 0 })}
                    className="font-mono text-right tabular-nums" data-testid={`pw-tender-${i}-fees`} />
                </div>
                <div className="space-y-1.5">
                  <Label className="eyebrow">Tender EMD (₹)</Label>
                  <Input type="number" min={0} step="0.01" value={t.emd}
                    onChange={(e) => setTender(i, { emd: Number(e.target.value) || 0 })}
                    className="font-mono text-right tabular-nums" data-testid={`pw-tender-${i}-emd`} />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="eyebrow">Tender Reference Number</Label>
                  <Input value={t.reference_number}
                    onChange={(e) => setTender(i, { reference_number: e.target.value })}
                    placeholder="e.g. E Tender No 2/2026-27"
                    className="font-mono" data-testid={`pw-tender-${i}-refno`} />
                </div>
                <div className="space-y-1.5">
                  <Label className="eyebrow">Organisation Chain</Label>
                  <Input value={t.organisation_chain}
                    onChange={(e) => setTender(i, { organisation_chain: e.target.value })}
                    placeholder="Dept — Office — Location"
                    className="font-mono" data-testid={`pw-tender-${i}-org`} />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="eyebrow">Publish Date &amp; Time <span className="text-[10px] font-normal text-muted-foreground normal-case tracking-normal ml-1">(any date — past OK)</span></Label>
                  <Input type="datetime-local" min="1990-01-01T00:00" value={t.publish_date}
                    onChange={(e) => setTender(i, { publish_date: e.target.value })}
                    className="font-mono" data-testid={`pw-tender-${i}-publish`} />
                </div>
                <div className="space-y-1.5">
                  <Label className="eyebrow">Last Date of Tender <span className="text-[10px] font-normal text-muted-foreground normal-case tracking-normal ml-1">(date &amp; time)</span></Label>
                  <Input type="datetime-local" min="1990-01-01T00:00" value={t.last_date}
                    onChange={(e) => setTender(i, { last_date: e.target.value })}
                    className="font-mono" data-testid={`pw-tender-${i}-last`} />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="eyebrow">Bid Opening Date &amp; Time</Label>
                  <Input type="datetime-local" min="1990-01-01T00:00" value={t.bid_opening_date}
                    onChange={(e) => setTender(i, { bid_opening_date: e.target.value })}
                    className="font-mono" data-testid={`pw-tender-${i}-bid-open-date`} />
                </div>
                <div className="space-y-1.5">
                  <Label className="eyebrow">Bid Opening Place</Label>
                  <Input value={t.bid_opening_place}
                    onChange={(e) => setTender(i, { bid_opening_place: e.target.value })}
                    placeholder="e.g. Office of Executive Engineer, Kolhapur"
                    className="font-mono" data-testid={`pw-tender-${i}-bid-open-place`} />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function emptyLine() {
  return { item_type: "other", item_id: "", item_name: "", quantity: 1, amount: 0, tax_rate: 0, description: "" };
}

// Free-form "supporting tenders" — a list of description-style textareas
// the operator adds alongside the structured MahaTenders entries. Each
// row is a plain string; the group grows with an "+ Add supporting
// tender" button and shrinks with a per-row remove.
function SupportingTenders({ f, setF }) {
  const items = f.supporting_tenders || [];
  const update = (i, val) =>
    setF((prev) => ({
      ...prev,
      supporting_tenders: (prev.supporting_tenders || []).map((s, idx) => (idx === i ? val : s)),
    }));
  const add = () =>
    setF((prev) => ({
      ...prev,
      supporting_tenders: [...(prev.supporting_tenders || []), ""],
    }));
  const remove = (i) =>
    setF((prev) => ({
      ...prev,
      supporting_tenders: (prev.supporting_tenders || []).filter((_, idx) => idx !== i),
    }));

  if (items.length === 0) return null;

  return (
    <div className="rounded-lg border border-border p-4 space-y-3" data-testid="pw-supporting-tenders">
      {items.map((val, i) => (
        <div key={i} className="rounded-md border border-primary/30 bg-primary/[.04] p-3 space-y-2"
             data-testid={`pw-supporting-card-${i}`}>
          <div className="flex items-center justify-between">
            <div className="eyebrow" style={{ color: "hsl(var(--primary))" }}>Supporting Company {i + 1}</div>
            <Button type="button" variant="ghost" size="icon" className="h-7 w-7 text-destructive"
                    onClick={() => remove(i)} data-testid={`pw-supporting-remove-${i}`}>
              <Trash size={13} />
            </Button>
          </div>
          <Input
            value={val}
            onChange={(e) => update(i, e.target.value)}
            placeholder={`Supporting company name ${i + 1}`}
            data-testid={`pw-supporting-text-${i}`}
          />
        </div>
      ))}
    </div>
  );
}

// Compact tender shortcut shown right below the customer email/mobile row
// so the operator can paste a MahaTenders URL and add tenders without
// scrolling to the tender section. Auto-turns the "Government Tender"
// toggle on so `TenderSection` renders the newly-added / fetched tender.
function TenderQuickBar({ f, setF }) {
  const [pasted, setPasted] = useState("");
  const [busy, setBusy] = useState(false);

  const pushTender = (patch) => {
    setF((prev) => ({
      ...prev,
      is_tender: true,
      tenders: [...(prev.tenders || []), { ...emptyTender(), ...(patch || {}) }],
    }));
  };

  const fetchOne = async () => {
    const url = pasted.trim();
    if (!url) { toast.error("Paste a MahaTenders tender detail URL first"); return; }
    if (!url.startsWith("https://mahatenders.gov.in/")) {
      toast.error("URL must be from mahatenders.gov.in"); return;
    }
    setBusy(true);
    const t = toast.loading("Fetching tender from URL…", { duration: 30000 });
    try {
      const { data } = await api.post("/tenders/scrape-url", { tender_detail_url: url });
      toast.dismiss(t);
      pushTender({
        tender_id: data.tender_id || "",
        name_of_work: data.name_of_work || "",
        value: Number(data.tender_value) || 0,
        fees: Number(data.tender_fees) || 0,
        emd: Number(data.tender_emd) || 0,
        publish_date: data.publish_date || "",
        last_date: data.last_date || "",
        organisation_chain: data.organisation || "",
        reference_number: data.reference_number || "",
        bid_opening_date: data.bid_opening_date || "",
        bid_opening_place: data.bid_opening_place || "",
      });
      const filled = [
        data.name_of_work && "Name",
        Number(data.tender_value) && "Value",
        data.publish_date && "Publish",
        data.last_date && "Last Date",
      ].filter(Boolean);
      toast.success(filled.length ? `Tender added — Fetched: ${filled.join(", ")}` : "Tender added — details couldn't be parsed, fill manually below");
      setPasted("");
    } catch (err) {
      toast.dismiss(t); toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-md border border-primary/30 bg-primary/[.04] p-2.5 flex items-stretch gap-2 flex-wrap"
         data-testid="pw-tender-quickbar">
      <Input
        value={pasted}
        onChange={(e) => setPasted(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); fetchOne(); } }}
        placeholder="Shortcut: paste MahaTenders tender URL → auto-fill"
        className="font-mono flex-1 min-w-[220px]"
        data-testid="pw-tender-quick-url"
      />
      <Button type="button" onClick={fetchOne} disabled={busy}
              data-testid="pw-tender-quick-fetch">
        {busy ? "Fetching…" : "Fetch"}
      </Button>
      <Button type="button" variant="outline"
              onClick={() => window.open("https://mahatenders.gov.in/nicgep/app", "_blank", "noopener")}
              data-testid="pw-tender-quick-portal">
        Open MahaTenders →
      </Button>
      <Button type="button" variant="outline"
              onClick={() => setF((prev) => ({ ...prev, supporting_tenders: [...(prev.supporting_tenders || []), ""] }))}
              data-testid="pw-supporting-add">
        <Plus size={13} className="mr-1" /> Add supporting tender
      </Button>
    </div>
  );
}

function emptyForm() {
  return {
    date: today(),
    party_type: "customer",
    customer_id: "",
    customer_name: "",
    customer_phone: "",
    customer_email: "",
    customer_state_code: "",
    customer_gstin: "",
    lines: [emptyLine()],
    description: "",
    deadline: inOneDay(),
    notes: "",
    is_tender: false,
    tender_id: "",
    tender_name_of_work: "",
    tender_value: 0,
    tender_fees: 0,
    tender_emd: 0,
    tender_publish_date: "",
    tender_last_date: "",
    tender_organisation_chain: "",
    tender_reference_number: "",
    tender_bid_opening_date: "",
    tender_bid_opening_place: "",
    tenders: [],
    supporting_tenders: [],
    deadline_tender_index: null,
  };
}

function WorkForm({ customers, products, services, editing, onClose, onSaved, onCustomerCreated }) {
  const { dateFloor } = useAuth();
  const [f, setF] = useState(() => {
    if (!editing) return emptyForm();
    // Legacy record: no `lines` field → synth one from top-level fields.
    let lines = editing.lines || [];
    if (!lines.length) {
      lines = [{
        item_type: editing.item_type || "other",
        item_id: editing.item_id || "",
        item_name: editing.item_name || "",
        quantity: editing.quantity || 1,
        amount: editing.amount || 0,
        tax_rate: editing.tax_rate || 0,
        description: (editing.descriptions || [])[0] || "",
      }];
    }
    return {
      ...emptyForm(),
      ...editing,
      lines,
      description: (editing.descriptions || []).join("\n") || editing.description || "",
    };
  });
  const [saving, setSaving] = useState(false);
  const [newCust, setNewCust] = useState(false);
  const [nc, setNc] = useState({ name: "", phone: "", address: "", email: "", state_code: "" });

  const setLine = (i, patch) => setF((prev) => ({ ...prev, lines: prev.lines.map((ln, idx) => idx === i ? { ...ln, ...patch } : ln) }));
  const addLine = () => setF((prev) => ({ ...prev, lines: [...prev.lines, emptyLine()] }));
  const rmLine = (i) => setF((prev) => ({ ...prev, lines: prev.lines.length > 1 ? prev.lines.filter((_, idx) => idx !== i) : prev.lines }));

  const pickLineItem = (i, v) => {
    if (!v || v === "none") { setLine(i, { item_type: "other", item_id: "", item_name: "" }); return; }
    const [type, id] = v.split(":");
    const list = type === "product" ? products : services;
    const it = list.find((x) => x.id === id);
    const rate = type === "product" ? (it?.sale_price || 0) : (it?.price || 0);
    const tax = it?.tax_rate ?? 0;
    setLine(i, {
      item_type: type,
      item_id: id,
      item_name: it?.name || "",
      amount: f.lines[i].amount || rate,
      tax_rate: f.lines[i].tax_rate || tax,
    });
  };

  const linesGrandTotal = useMemo(
    () => f.lines.reduce((s, ln) => s + (Number(ln.amount) || 0) * Math.max(1, Number(ln.quantity) || 1), 0),
    [f.lines]
  );

  const pickCustomer = (id) => {
    if (id === "__new__") { setNewCust(true); setF({ ...f, customer_id: "", customer_name: "", customer_phone: "", customer_email: "", customer_state_code: "", customer_gstin: "" }); return; }
    setNewCust(false);
    const c = customers.find((x) => x.id === id);
    setF({
      ...f, customer_id: id, customer_name: c?.name || "",
      customer_phone: c?.phone || "",
      customer_email: c?.email || "",
      customer_state_code: c?.state_code || "", customer_gstin: c?.gstin || "",
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    let payloadCustomerId = f.customer_id;
    let payloadCustomerName = f.customer_name;

    if (f.party_type === "customer" && newCust) {
      // Create customer on the fly
      if (!nc.name.trim()) { toast.error("Customer name is required"); return; }
      try {
        const { data } = await api.post("/customers", nc);
        payloadCustomerId = data.id;
        payloadCustomerName = data.name;
        onCustomerCreated?.(data);
        toast.success(`New customer "${data.name}" saved`);
      } catch (err) { toast.error(formatApiError(err)); return; }
    } else if (f.party_type === "customer" && !f.customer_id) {
      toast.error("Select customer or add a new one"); return;
    } else if (f.party_type === "other" && !f.customer_name.trim()) {
      toast.error("Enter a name"); return;
    }
    if (!f.deadline) { toast.error("Set deadline date & time"); return; }

    const descText = (f.description || "").trim();
    // Sanitize lines: drop rows with no item and no amount, but keep at least one row.
    const cleanLines = (f.lines || []).map((ln) => ({
      item_type: ln.item_type || "other",
      item_id: ln.item_id || "",
      item_name: (ln.item_name || "").trim(),
      quantity: Math.max(1, parseInt(ln.quantity, 10) || 1),
      amount: Number(ln.amount) || 0,
      tax_rate: Number(ln.tax_rate) || 0,
      description: (ln.description || "").trim(),
    })).filter((ln) => ln.item_name || ln.amount > 0 || ln.description);
    const payload = {
      ...f,
      customer_id: payloadCustomerId,
      customer_name: payloadCustomerName,
      descriptions: descText ? [descText] : [],
      lines: cleanLines.length ? cleanLines : [emptyLine()],
    };
    delete payload.description;
    setSaving(true);
    try {
      if (editing?.id) {
        await api.patch(`/pending-works/${editing.id}`, payload);
        toast.success("Work updated");
      } else {
        await api.post("/pending-works", payload);
        toast.success("Work added");
      }
      onSaved();
      onClose();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label className="eyebrow">Date</Label>
          <Input type="date" min={dateFloor} value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} className="font-mono" data-testid="pw-date" />
        </div>
        <div className="space-y-1.5">
          <Label className="eyebrow">Deadline (Last Date & Time) *</Label>
          <Input type="datetime-local" required value={f.deadline}
            onChange={(e) => setF({ ...f, deadline: e.target.value })}
            readOnly={!!f.is_tender && (f.tenders || []).length === 1}
            className={`font-mono ${f.is_tender && (f.tenders || []).length === 1 ? "bg-muted/40 cursor-not-allowed" : ""}`}
            data-testid="pw-deadline" />
          {f.is_tender && (f.tenders || []).length === 1 && (
            <div className="text-[10.5px] text-muted-foreground">
              Auto-mirrored from the tender's Last Date.
            </div>
          )}
          {f.is_tender && (f.tenders || []).length >= 2 && (
            <div className="text-[10.5px] text-amber-700 dark:text-amber-300 font-medium">
              ⚠️ Please select the Deadline date & time manually.
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="space-y-1.5">
          <Label className="eyebrow">Name of…</Label>
          <Select value={f.party_type} onValueChange={(v) => setF({ ...f, party_type: v, customer_id: "", customer_name: "" })}>
            <SelectTrigger data-testid="pw-party-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="customer">Customer</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5 md:col-span-2">
          <Label className="eyebrow">{f.party_type === "customer" ? "Customer *" : "Name *"}</Label>
          {f.party_type === "customer" ? (
            <SearchableCustomerSelect
              value={newCust ? "__new__" : f.customer_id}
              onChange={pickCustomer}
              customers={customers}
              createValue="__new__"
              createLabel="Add new customer manually…"
              placeholder="Choose customer"
              testid="pw-customer"
              getSubtitle={(c) => c.phone}
            />
          ) : (
            <Input value={f.customer_name}
              onChange={(e) => setF({ ...f, customer_name: e.target.value })}
              placeholder="e.g. Bank visit, Ravi contractor…" data-testid="pw-party-name" />
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label className="eyebrow">Mobile Number {f.party_type === "customer" && f.customer_id ? "(auto-filled)" : ""}</Label>
          <Input
            inputMode="tel"
            value={f.customer_phone || ""}
            onChange={(e) => setF({ ...f, customer_phone: e.target.value.replace(/[^0-9+\- ]/g, "") })}
            placeholder="10-digit mobile"
            className="font-mono"
            data-testid="pw-phone"
          />
        </div>
        <div className="space-y-1.5">
          <Label className="eyebrow">Email {f.party_type === "customer" && f.customer_id ? "(auto-filled)" : ""}</Label>
          <Input
            type="email"
            value={f.customer_email || ""}
            onChange={(e) => setF({ ...f, customer_email: e.target.value.trim() })}
            placeholder="name@example.com"
            className="font-mono"
            data-testid="pw-email"
          />
        </div>
      </div>

      <TenderQuickBar f={f} setF={setF} />

      {f.party_type === "customer" && newCust && (
        <div className="card-flat p-4 space-y-3 border-l-4" style={{ borderLeftColor: "hsl(var(--primary))" }}>
          <div className="flex items-center justify-between">
            <div className="eyebrow" style={{ color: "hsl(var(--primary))" }}>New Customer Details</div>
            <Button type="button" variant="ghost" size="sm" className="h-7 text-[11px]"
              onClick={() => { setNewCust(false); setNc({ name: "", phone: "", address: "", email: "", state_code: "" }); }}>
              ← Back to list
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="eyebrow">Customer Name *</Label>
              <Input required value={nc.name} onChange={(e) => setNc({ ...nc, name: e.target.value })}
                data-testid="pw-newcust-name" />
            </div>
            <div className="space-y-1.5">
              <Label className="eyebrow">Mobile Number</Label>
              <Input inputMode="tel" value={nc.phone}
                onChange={(e) => setNc({ ...nc, phone: e.target.value })}
                placeholder="10-digit mobile (optional)" data-testid="pw-newcust-phone" />
            </div>
            <div className="space-y-1.5">
              <Label className="eyebrow">Email</Label>
              <Input type="email" value={nc.email} onChange={(e) => setNc({ ...nc, email: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="eyebrow">State Code</Label>
              <Input value={nc.state_code} onChange={(e) => setNc({ ...nc, state_code: e.target.value })}
                placeholder="e.g. 33" />
            </div>
            <div className="space-y-1.5 md:col-span-2">
              <Label className="eyebrow">Address</Label>
              <Textarea rows={2} value={nc.address}
                onChange={(e) => setNc({ ...nc, address: e.target.value })}
                placeholder="Optional"
                data-testid="pw-newcust-address" />
            </div>
          </div>
          <div className="text-[11px] text-muted-foreground">The customer will be saved to the master list when you save this work.</div>
        </div>
      )}

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div>
            <Label className="eyebrow">Products / Services / Works</Label>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              Add one row per product or service. On <span className="font-semibold text-foreground">Completed</span>, a single
              temp invoice <span className="font-mono">VT/TEMP/nnnn</span> is generated with all lines.
            </p>
          </div>
          <Button type="button" size="sm" variant="outline" className="h-8" onClick={addLine} data-testid="pw-add-line">
            <Plus size={12} className="mr-1" /> Add line
          </Button>
        </div>
        <div className="rounded border border-border overflow-hidden">
          <table className="w-full text-[12.5px]">
            <thead className="bg-muted/40">
              <tr>
                <th className="text-left px-2.5 py-2 font-semibold w-[32%]">Product / Service / Other</th>
                <th className="text-right px-2 py-2 font-semibold w-[13%]">Qty</th>
                <th className="text-right px-2 py-2 font-semibold w-[18%]">Rate (₹)</th>
                <th className="text-right px-2 py-2 font-semibold w-[13%]">Tax %</th>
                <th className="text-right px-2 py-2 font-semibold w-[18%]">Line Total</th>
                <th className="px-1 py-2 w-[6%]"></th>
              </tr>
            </thead>
            <tbody>
              {f.lines.map((ln, i) => {
                const lineTotal = (Number(ln.amount) || 0) * Math.max(1, Number(ln.quantity) || 1);
                return (
                  <tr key={i} className="border-t border-border align-top">
                    <td className="px-2.5 py-2 space-y-1.5">
                      <SearchableItemSelect
                        value={ln.item_id ? `${ln.item_type}:${ln.item_id}` : "none"}
                        onChange={(v) => pickLineItem(i, v)}
                        products={products}
                        services={services}
                        noneValue="none"
                        noneLabel="Other / General work"
                        placeholder="Choose product / service / other"
                        testid={`pw-line-${i}-item`}
                      />
                      {(!ln.item_id) && (
                        <Input
                          value={ln.item_name}
                          onChange={(e) => setLine(i, { item_name: e.target.value })}
                          placeholder="Item name / short label"
                          className="h-8"
                          data-testid={`pw-line-${i}-name`}
                        />
                      )}
                    </td>
                    <td className="px-2 py-2">
                      <Input type="number" min={1} step={1} value={ln.quantity}
                        onChange={(e) => setLine(i, { quantity: Math.max(1, parseInt(e.target.value || "1", 10)) })}
                        className="font-mono h-8 text-right min-w-[80px] tabular-nums" data-testid={`pw-line-${i}-qty`} />
                    </td>
                    <td className="px-2 py-2">
                      <Input type="number" min={0} step="0.01" value={ln.amount}
                        onChange={(e) => setLine(i, { amount: Number(e.target.value) || 0 })}
                        placeholder="0.00"
                        className="font-mono h-8 text-right min-w-[110px] tabular-nums" data-testid={`pw-line-${i}-rate`} />
                    </td>
                    <td className="px-2 py-2">
                      <Input type="number" min={0} max={28} step="0.01" value={ln.tax_rate}
                        onChange={(e) => setLine(i, { tax_rate: Number(e.target.value) || 0 })}
                        className="font-mono h-8 text-right min-w-[70px] tabular-nums" data-testid={`pw-line-${i}-tax`} />
                    </td>
                    <td className="px-2 py-2 text-right font-mono">{inr(lineTotal)}</td>
                    <td className="px-1 py-2 text-right">
                      {f.lines.length > 1 && (
                        <Button type="button" variant="ghost" size="icon" className="h-7 w-7 text-destructive"
                          onClick={() => rmLine(i)} data-testid={`pw-line-${i}-remove`}>
                          <Trash size={13} />
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="border-t border-border bg-muted/30">
                <td colSpan={4} className="px-2.5 py-2 text-right eyebrow">Grand Total (billable)</td>
                <td className="px-2 py-2 text-right font-mono font-semibold" data-testid="pw-lines-total">{inr(linesGrandTotal)}</td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      <SupportingTenders f={f} setF={setF} />

      <div className="space-y-2">
        <Label className="eyebrow">Description / Name of Work / Other Service Specify</Label>
        <Textarea
          rows={4}
          value={f.description}
          onChange={(e) => setF({ ...f, description: e.target.value })}
          placeholder="e.g. GST filing Q1, DSC renewal, Company incorporation…"
          data-testid="pw-desc"
        />
      </div>

      <TenderSection f={f} setF={setF} />

      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
        <Button type="submit" disabled={saving} data-testid="pw-save-btn">{saving ? "Saving…" : (editing?.id ? "Update Work" : "Add Work")}</Button>
      </div>
    </form>
  );
}

function PostponeDialog({ row, onClose, onDone }) {
  const [when, setWhen] = useState(row.deadline?.slice(0, 16) || inOneDay());
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!when) { toast.error("Pick a new date & time"); return; }
    setSaving(true);
    try {
      await api.post(`/pending-works/${row.id}/status`, { status: "postponed", new_deadline: when, reason });
      toast.success(`Work postponed to ${fmtDeadline(when)}`);
      onDone();
      onClose();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };
  return (
    <div className="space-y-4">
      <div className="text-sm text-muted-foreground">
        Current deadline: <span className="font-mono">{fmtDeadline(row.deadline)}</span>
      </div>
      <div className="space-y-1.5">
        <Label className="eyebrow">New deadline *</Label>
        <Input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)}
          className="font-mono" data-testid="pw-postpone-date" />
      </div>
      <div className="space-y-1.5">
        <Label className="eyebrow">Reason (optional)</Label>
        <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
      </div>
      <div className="flex justify-end gap-2 pt-2">
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button onClick={submit} disabled={saving} data-testid="pw-postpone-save">
          {saving ? "Saving…" : "Postpone Work"}
        </Button>
      </div>
    </div>
  );
}

export default function PendingWorks() {
  const { activeCompanyId, activeCompany, activeStore, can, activeStoreId, dateFloor } = useAuth();
  const [tab, setTab] = useState("pending");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [services, setServices] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [postponing, setPostponing] = useState(null);
  const [statementEmailOpen, setStatementEmailOpen] = useState(false);
  const [statementEmailTo, setStatementEmailTo] = useState("");
  const [statementEmailSending, setStatementEmailSending] = useState(false);
  // Date-range filter on the `date` (created) field. Empty = no bound.
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Force re-render every minute so the overdue banner keeps time in sync
  // even if the user leaves the tab open past the deadline.
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 60 * 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!activeCompanyId) return;
    Promise.all([
      api.get("/customers"),
      api.get("/products"),
      api.get("/services").catch(() => ({ data: [] })),
    ]).then(([c, p, s]) => {
      setCustomers(c.data); setProducts(p.data); setServices(s.data || []);
    });
  }, [activeCompanyId, activeStoreId]);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ status: tab });
      if (startDate) params.set("start_date", startDate);
      if (endDate) params.set("end_date", endDate);
      const { data } = await api.get(`/pending-works?${params.toString()}`);
      setRows(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { if (activeCompanyId) load(); }, [activeCompanyId, activeStoreId, tab, startDate, endDate]);

  const applyPreset = (preset) => {
    const d = new Date();
    const iso = (x) => x.toISOString().slice(0, 10);
    if (preset === "today") {
      const t = iso(d); setStartDate(t); setEndDate(t);
    } else if (preset === "week") {
      const end = new Date(d);
      const start = new Date(d); start.setDate(start.getDate() - 6);
      setStartDate(iso(start)); setEndDate(iso(end));
    } else if (preset === "month") {
      const start = new Date(d.getFullYear(), d.getMonth(), 1);
      const end = new Date(d.getFullYear(), d.getMonth() + 1, 0);
      setStartDate(iso(start)); setEndDate(iso(end));
    } else if (preset === "clear") {
      setStartDate(""); setEndDate("");
    }
  };

  const setStatus = async (row, status) => {
    try {
      const { data } = await api.post(`/pending-works/${row.id}/status`, { status });
      if (status === "completed" && data?.temp_invoice_number) {
        toast.success(`Completed. Auto-created ${data.temp_invoice_number} in Payment Pending.`, { duration: 6000 });
      } else {
        toast.success(status === "completed" ? "Marked completed" : status === "cancelled" ? "Cancelled" : "Reopened");
      }
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const del = async (id) => {
    if (!confirm("Delete this work entry permanently?")) return;
    try { await api.delete(`/pending-works/${id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const _fmtDeadlineShort = (dl) => fmtDateTime(dl, "");

  const _buildReminderText = (r) => {
    const compName = activeCompany?.name || "Vetrich";
    const lineSummary = (r.lines || []).slice(0, 5).map((ln) => `• ${ln.item_name || "—"} × ${ln.quantity || 1}`).join("\n");
    const more = (r.lines || []).length > 5 ? `\n…and ${r.lines.length - 5} more` : "";
    return [
      `*${compName} — Deadline Reminder*`,
      "",
      `Hi ${r.customer_name || "Customer"},`,
      "",
      `Friendly reminder that the following work is due by *${_fmtDeadlineShort(r.deadline)}*:`,
      lineSummary + more,
      "",
      "Please let us know if you need to reschedule or share any pending documents.",
      "",
      `— ${compName}`,
    ].filter(Boolean).join("\n");
  };

  const sendWhatsAppReminder = (r) => {
    const phone = String(r.customer_phone || "").replace(/[^0-9]/g, "");
    if (!phone) {
      toast.error("No mobile number on file. Click Edit and add one before sending a WhatsApp reminder.", { duration: 6000 });
      return;
    }
    // Auto-prefix 91 if the number looks like a bare 10-digit Indian mobile
    const waPhone = phone.length === 10 ? `91${phone}` : phone;
    const url = `https://wa.me/${waPhone}?text=${encodeURIComponent(_buildReminderText(r))}`;
    window.open(url, "_blank", "noopener,noreferrer");
    toast.success(`WhatsApp opened for ${phone}`);
  };

  const sendEmailReminder = async (r) => {
    if (!r.customer_email) {
      toast.error("No email on file. Click Edit and add one before sending an email reminder.", { duration: 6000 });
      return;
    }
    try {
      const { data } = await api.post(`/pending-works/${r.id}/remind`, {});
      toast.success(`Reminder sent to ${data.sent_to}`, { duration: 5000 });
    } catch (err) { toast.error(formatApiError(err)); }
  };

  // ── Statement-wide sharing (WhatsApp + Email of the whole current tab) ──
  const sendStatementWhatsApp = async () => {
    if (!rows.length) { toast.error(`No ${tab} works to share.`); return; }
    const compName = activeCompany?.name || "Company";
    const compWa = String(activeCompany?.whatsapp_number || "").replace(/[^0-9]/g, "");
    const storeName = activeStore?.name ? ` · ${activeStore.name}` : "";
    const totalAmt = rows.reduce((s, r) => s + (Number(r.amount) || 0), 0);

    // 1. Create a public share snapshot on the server (returns id + token)
    let publicUrl = "";
    try {
      const { data: snap } = await api.post("/pending-works/share", { status: tab });
      publicUrl = `${window.location.origin}/p/pending-works/${snap.id}?t=${encodeURIComponent(snap.token)}`;
    } catch (err) {
      toast.error(formatApiError(err));
      return;
    }

    // 2. Compose the WhatsApp text with the link + a compact summary
    const lines = rows.slice(0, 10).map((r) => {
      const item = (r.lines && r.lines.length)
        ? r.lines.map((ln) => `${ln.item_name || "—"}×${ln.quantity || 1}`).join(", ")
        : (r.item_name || "—");
      const amt = r.amount ? ` — ${inr(r.amount)}` : "";
      return `  • ${r.customer_name || "—"} — ${item}${amt}`;
    });
    const extra = rows.length > 10 ? `\n  …and ${rows.length - 10} more (see link)` : "";
    const text = [
      `*${compName}${storeName} — Daily Pending Works (${tab.charAt(0).toUpperCase() + tab.slice(1)})*`,
      `Total: *${rows.length}* task(s) · *${inr(totalAmt)}*`,
      "",
      ...lines,
      extra,
      "",
      `📄 View full statement + download PDF:`,
      publicUrl,
    ].filter(Boolean).join("\n");

    // 3. Open WhatsApp — recipient taps the link → sees clean statement + Download PDF button
    const encoded = encodeURIComponent(text);
    const url = compWa ? `https://wa.me/${compWa}?text=${encoded}` : `https://wa.me/?text=${encoded}`;
    window.open(url, "_blank", "noopener,noreferrer");
    toast.success("WhatsApp opened with a shareable statement link.", { duration: 6000 });
  };

  const sendStatementEmail = async () => {
    setStatementEmailSending(true);
    try {
      const overrides = statementEmailTo
        .split(/[\s,;]+/)
        .map((e) => e.trim())
        .filter((e) => e && e.includes("@"));
      const { data: res } = await api.post("/pending-works/email-statement", {
        status: tab,
        recipients: overrides.length ? overrides : undefined,
      });
      if (res?.ok && res.sent_count > 0) {
        toast.success(`Statement emailed to ${res.sent_count} recipient${res.sent_count > 1 ? "s" : ""}`, { duration: 6000 });
        setStatementEmailOpen(false);
        setStatementEmailTo("");
      } else {
        toast.error((res?.errors || []).join(" · ") || "Email failed");
      }
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setStatementEmailSending(false); }
  };

  const isOverdue = (dl) => {
    if (!dl) return false;
    const d = new Date(dl); return !isNaN(d) && d < new Date();
  };

  // Overdue works still in "pending" or "postponed" state — used for the big red banner.
  // Recomputes on every render (and the 1-min tick keeps things fresh).
  const overdueWorks = useMemo(() => {
    const now = new Date();
    return rows
      .filter((r) => (r.status === "pending" || r.status === "postponed") && r.deadline)
      .map((r) => {
        const d = new Date(r.deadline);
        const overdueMs = now - d;
        return { ...r, overdueMs, deadlineDate: d };
      })
      .filter((r) => r.overdueMs > 0 && !isNaN(r.deadlineDate))
      .sort((a, b) => b.overdueMs - a.overdueMs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, tick]);

  // Centered overdue alert modal — shows once per session while overdues exist.
  // Hidden after user clicks OK; reappears next session or after refresh.
  const [overdueAlertOpen, setOverdueAlertOpen] = useState(false);
  const [overdueAlertDismissed, setOverdueAlertDismissed] = useState(
    () => sessionStorage.getItem("vt_overdue_alert_dismissed") === "1"
  );
  useEffect(() => {
    if (overdueWorks.length > 0 && !overdueAlertDismissed) {
      setOverdueAlertOpen(true);
    } else if (overdueWorks.length === 0) {
      setOverdueAlertOpen(false);
    }
  }, [overdueWorks.length, overdueAlertDismissed]);
  const dismissOverdueAlert = () => {
    sessionStorage.setItem("vt_overdue_alert_dismissed", "1");
    setOverdueAlertDismissed(true);
    setOverdueAlertOpen(false);
  };
  const humanOverdue = (ms) => {
    const mins = Math.floor(ms / 60000);
    if (mins < 60) return `${mins} min${mins !== 1 ? "s" : ""}`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs} hr${hrs !== 1 ? "s" : ""} ${mins % 60} min`;
    const days = Math.floor(hrs / 24);
    return `${days} day${days !== 1 ? "s" : ""} ${hrs % 24} hr`;
  };

  const exportExcel = () => {
    if (!rows.length) { toast.error(`No ${tab} works to export.`); return; }
    downloadCSV(`daily-pending-works-${tab}-${today()}`, [
      { key: "id", label: "id" },   // keep first so re-import can round-trip (upsert by id)
      { key: "date", label: "date" },
      { key: "party_type", label: "party_type" },
      { key: "customer_name", label: "customer_name" },
      { key: "item_type", label: "item_type" },
      { key: "item_name", label: "item_name" },
      { key: "quantity", label: "quantity" },
      { key: "amount", label: "amount" },
      { key: "tax_rate", label: "tax_rate" },
      { key: "descriptions", label: "descriptions",
        get: (r) => (r.descriptions || []).join(" | ") },
      { key: "lines", label: "lines",
        get: (r) => (r.lines || []).map((ln) => `${ln.item_type}:${ln.item_name || ""}:qty${ln.quantity}:amt${ln.amount}:tax${ln.tax_rate}`).join(" | ") },
      { key: "deadline", label: "deadline" },
      { key: "status", label: "status" },
      { key: "notes", label: "notes" },
    ], rows);
    toast.success(`Exported ${rows.length} row(s) — re-import this file anytime to restore or update`);
  };

  const printStatement = () => {
    if (!rows.length) { toast.error(`No ${tab} works to print.`); return; }
    const cols = [
      { key: "date", label: "Created", mono: true },
      { key: "party", label: "Party" },
      { key: "item", label: "Item" },
      { key: "description", label: "Description" },
      { key: "deadline", label: "Deadline", mono: true },
      { key: "amount", label: "Amount", align: "right", mono: true },
    ];
    const buildRow = (r) => ({
      date: r.date || "—",
      party: (r.customer_name || "—") + (r.party_type === "other" ? " (Other)" : ""),
      item: (r.lines && r.lines.length)
        ? r.lines.map((ln) => `${ln.item_type}·${ln.item_name || "—"}×${ln.quantity}`).join(" + ")
        : (r.item_name ? `${r.item_type} · ${r.item_name}` : "—"),
      description: (r.descriptions || []).join(" · ") || "—",
      deadline: fmtDeadline(r.deadline),
      amount: r.amount ? inr(r.amount) : "—",
    });

    let sections = [];
    if (tab === "pending") {
      const overdue = rows.filter((r) => isOverdue(r.deadline));
      const upcoming = rows.filter((r) => !isOverdue(r.deadline));
      if (overdue.length) sections.push({
        heading: "Overdue (Deadline Crossed)",
        subheading: `${overdue.length} task(s) requiring immediate action`,
        columns: cols.map((c) => c.key === "deadline" ? { ...c, tone: "neg" } : c),
        rows: overdue.map(buildRow),
      });
      if (upcoming.length) sections.push({
        heading: "Upcoming Deadlines",
        subheading: `${upcoming.length} task(s) still within deadline`,
        columns: cols, rows: upcoming.map(buildRow),
      });
    } else {
      sections.push({
        heading: tab === "completed" ? "Completed Works" : "Cancelled Works",
        subheading: `${rows.length} task(s)`,
        columns: cols, rows: rows.map(buildRow),
      });
    }

    const totalAmount = rows.reduce((sum, r) => sum + (Number(r.amount) || 0), 0);
    const res = openPrintStatement({
      title: `Daily Pending Works — ${tab.charAt(0).toUpperCase() + tab.slice(1)}`,
      company: activeCompany || {},
      sections,
      totals: [
        { label: "Total Tasks", value: String(rows.length) },
        { label: "Total Amount", value: inr(totalAmount) },
        ...(tab === "pending" ? [{
          label: "Overdue",
          value: String(rows.filter((r) => isOverdue(r.deadline)).length),
          tone: "neg",
        }] : []),
      ],
      note: tab === "pending"
        ? "Complete or postpone overdue tasks — reminders keep firing until every task is closed."
        : "",
    });
    if (!res.ok) toast.error(res.error);
    else toast.success(`Preparing statement for ${rows.length} task(s)…`);
  };

  return (
    <div className="page-shell space-y-6">
      {/* 🚨 Centered overdue alert modal — with warning graphic + OK to dismiss */}
      <OverdueDialog open={overdueAlertOpen} onOpenChange={(v) => { if (!v) dismissOverdueAlert(); }}>
        <OverdueDialogContent className="max-w-lg text-center border-2 border-destructive/60" data-testid="pw-overdue-modal">
          <OverdueGraphic />
          <div className="font-heading text-2xl font-black tracking-tight uppercase text-destructive mt-2">
            You may lose your work!
          </div>
          <div className="text-[14px] text-foreground/90 max-w-md mx-auto leading-relaxed">
            <b>{overdueWorks.length}</b> pending task{overdueWorks.length !== 1 ? "s have" : " has"} crossed the deadline.
            Complete or postpone them now — otherwise the work window closes and revenue leaks.
          </div>
          <div className="mt-3 max-h-40 overflow-auto rounded-md border border-destructive/40 bg-destructive/5 text-left px-3 py-2 text-[12px] space-y-1">
            {overdueWorks.slice(0, 6).map((r) => (
              <div key={r.id} className="flex items-center justify-between gap-2">
                <span className="truncate">
                  <b>{r.item_name || "Work"}</b>
                  <span className="text-muted-foreground"> · {r.customer_name}</span>
                </span>
                <span className="font-mono text-destructive shrink-0">
                  overdue {humanOverdue(r.overdueMs)}
                </span>
              </div>
            ))}
            {overdueWorks.length > 6 && (
              <div className="text-[11px] text-muted-foreground pt-1">…and {overdueWorks.length - 6} more below.</div>
            )}
          </div>
          <div className="pt-3">
            <Button
              size="lg"
              className="h-11 px-8 min-w-[140px] uppercase tracking-widest font-bold"
              onClick={dismissOverdueAlert}
              data-testid="pw-overdue-modal-ok"
            >
              OK — Got it
            </Button>
          </div>
          <div className="text-[10.5px] text-muted-foreground pt-1">
            The list below stays visible until every task is completed.
          </div>
        </OverdueDialogContent>
      </OverdueDialog>

      {/* ⚠️ Big red "You may LOSE your work" banner — deadline crossed */}
      {overdueWorks.length > 0 && (
        <div
          className="rounded-lg border-2 border-destructive bg-destructive/10 p-5 space-y-3 shadow-md"
          data-testid="pw-loss-banner"
        >
          <div className="flex items-center gap-3">
            <Warning size={26} weight="fill" className="text-destructive shrink-0" />
            <div>
              <div className="font-heading text-xl font-bold text-destructive uppercase tracking-tight">
                You may lose your work
              </div>
              <div className="text-[13px] text-destructive/80 mt-0.5">
                {overdueWorks.length} task{overdueWorks.length !== 1 ? "s have" : " has"} crossed the deadline and still not completed. Take action now or the work will be lost.
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {overdueWorks.slice(0, 8).map((r) => (
              <div key={r.id} className="rounded-md border border-destructive/40 bg-background px-3 py-2 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-[13.5px] font-semibold truncate">
                    {r.item_name || "Work"}
                    <span className="ml-2 text-muted-foreground text-[11.5px]">· {r.customer_name}</span>
                  </div>
                  <div className="text-[11.5px] text-destructive font-mono">
                    Deadline was {fmtDeadline(r.deadline)} · overdue by <b>{humanOverdue(r.overdueMs)}</b>
                  </div>
                </div>
                {tab === "pending" && (
                  <div className="flex gap-1.5 shrink-0">
                    <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => setPostponing(r)}>
                      Postpone
                    </Button>
                    <Button size="sm" className="h-7 text-[11px]" onClick={() => setStatus(r, "completed")}>
                      Complete
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
          {overdueWorks.length > 8 && (
            <div className="text-[11.5px] text-destructive/80">
              …and {overdueWorks.length - 8} more. Scroll the table below to see the full list.
            </div>
          )}
        </div>
      )}

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="eyebrow">Task Tracker</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Add Daily Pending Works</h1>
          <p className="text-sm text-muted-foreground mt-1">Track tasks by deadline. Postpone, complete or cancel — reminders fire while work stays pending.</p>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}>
          <DialogTrigger asChild>
            <Button className="h-9" data-testid="pw-new-btn" onClick={() => setEditing(null)}>
              <Plus size={15} weight="bold" className="mr-1.5" /> New Work
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading tracking-tight flex items-center gap-2">
                <ClipboardText size={20} /> {editing ? "Edit Work" : "Add Daily Pending Work"}
              </DialogTitle>
            </DialogHeader>
            <WorkForm customers={customers} products={products} services={services}
              editing={editing} onClose={() => { setOpen(false); setEditing(null); }} onSaved={load}
              onCustomerCreated={(c) => setCustomers((prev) => [...prev, c])} />
          </DialogContent>
        </Dialog>
      </div>

      {/* Date-range filter toolbar */}
      <div className="flex items-center gap-2 flex-wrap card-flat px-3 py-2.5 -mt-2" data-testid="pw-date-filter">
        <div className="eyebrow text-[11px] mr-1">Filter by date</div>
        <div className="flex items-center gap-1.5">
          <Label className="text-[11px] text-muted-foreground">From</Label>
          <Input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="font-mono h-8 w-[150px] text-[12px]"
            data-testid="pw-filter-start"
          />
        </div>
        <div className="flex items-center gap-1.5">
          <Label className="text-[11px] text-muted-foreground">To</Label>
          <Input
            type="date"
            value={endDate}
            min={startDate || undefined}
            onChange={(e) => setEndDate(e.target.value)}
            className="font-mono h-8 w-[150px] text-[12px]"
            data-testid="pw-filter-end"
          />
        </div>
        <div className="flex items-center gap-1 ml-1">
          <Button variant="ghost" size="sm" className="h-7 text-[11px] px-2" onClick={() => applyPreset("today")} data-testid="pw-filter-today">Today</Button>
          <Button variant="ghost" size="sm" className="h-7 text-[11px] px-2" onClick={() => applyPreset("week")} data-testid="pw-filter-week">This Week</Button>
          <Button variant="ghost" size="sm" className="h-7 text-[11px] px-2" onClick={() => applyPreset("month")} data-testid="pw-filter-month">This Month</Button>
          {(startDate || endDate) && (
            <Button variant="outline" size="sm" className="h-7 text-[11px] px-2 ml-1" onClick={() => applyPreset("clear")} data-testid="pw-filter-clear">
              <XCircle size={12} className="mr-1" /> Clear
            </Button>
          )}
        </div>
        {(startDate || endDate) && (
          <div className="text-[11px] text-muted-foreground ml-auto font-mono" data-testid="pw-filter-summary">
            Showing {rows.length} · {startDate || "—"} → {endDate || "—"}
          </div>
        )}
      </div>

      <div className="flex items-center justify-end gap-2 -mt-2 flex-wrap">
        <Button variant="outline" className="h-9" onClick={printStatement}
          disabled={!can("pending_works", "export")}
          data-testid="pw-print-statement">
          <Printer size={16} className="mr-1.5" /> Print Statement
        </Button>
        <Button
          variant="outline"
          onClick={sendStatementWhatsApp}
          className="h-9 border-[#25D366] text-[#128C7E] hover:bg-[#25D366]/10 hover:text-[#128C7E]"
          data-testid="pw-send-whatsapp"
          title="Share the current tab's list on WhatsApp">
          <WhatsappLogo weight="fill" size={14} className="mr-1.5" /> WhatsApp Statement
        </Button>
        <Button
          variant="outline"
          onClick={() => setStatementEmailOpen(true)}
          className="h-9"
          data-testid="pw-send-email"
          title="Email the current tab's list as an HTML report">
          <EnvelopeSimple size={14} className="mr-1.5" /> Email Statement
        </Button>
        <Button variant="outline" className="h-9" onClick={exportExcel}
          disabled={!can("pending_works", "export")}
          data-testid="pw-export">
          <MicrosoftExcelLogo size={16} className="mr-1.5" /> Excel
        </Button>
        <BulkImportButton
          entity="pending-works"
          label="Import Excel"
          onDone={load}
          testid="pw-import"
        />
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="pw-tabs">
          <TabsTrigger value="pending" data-testid="pw-tab-pending">Pending</TabsTrigger>
          <TabsTrigger value="completed" data-testid="pw-tab-completed">Completed</TabsTrigger>
          <TabsTrigger value="cancelled" data-testid="pw-tab-cancelled">Cancelled</TabsTrigger>
        </TabsList>

        <TabsContent value={tab} className="mt-4">
          <div className="card-flat overflow-x-auto">
            <table className="data-table min-w-[900px]">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Party</th>
                  <th>Item</th>
                  <th>Description</th>
                  <th>Deadline</th>
                  <th className="num" style={{ textAlign: "right" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={6} className="text-center py-6 text-muted-foreground">Loading…</td></tr>
                ) : rows.length === 0 ? (
                  <tr><td colSpan={6} className="text-center py-10 text-muted-foreground">No {tab} works.</td></tr>
                ) : rows.map((r) => (
                  <tr key={r.id} data-testid={`pw-row-${r.id}`}>
                    <td className="font-mono text-[12px]">{fmtDate(r.date)}</td>
                    <td className="font-medium">
                      <div>{r.customer_name || "—"}</div>
                      {r.customer_phone && (
                        <div className="text-[11.5px] font-mono text-muted-foreground tracking-wide" data-testid={`pw-phone-${r.id}`}>
                          {r.customer_phone}
                        </div>
                      )}
                      {r.party_type === "other" && <span className="ml-0 pill pill-muted text-[10px]">Other</span>}
                    </td>
                    <td>
                      {(r.lines && r.lines.length > 0) ? (
                        <div className="space-y-0.5">
                          {r.lines.slice(0, 2).map((ln, i) => (
                            <div key={i} className="text-[12px] leading-tight">
                              <span className="pill pill-muted mr-1 text-[10px]">{ln.item_type}</span>
                              {ln.item_name || <span className="text-muted-foreground">—</span>}
                              <span className="text-muted-foreground font-mono ml-1">× {ln.quantity}</span>
                            </div>
                          ))}
                          {r.lines.length > 2 && (
                            <div className="text-[10.5px] text-muted-foreground">+{r.lines.length - 2} more line(s)</div>
                          )}
                          {r.amount > 0 && (
                            <div className="text-[11px] font-mono text-primary mt-0.5">Total {inr(r.amount)}</div>
                          )}
                        </div>
                      ) : r.item_name ? (
                        <>
                          <span className="pill pill-muted mr-1.5">{r.item_type}</span>
                          {r.item_name}
                        </>
                      ) : <span className="text-muted-foreground">—</span>}
                    </td>
                    <td className="text-[12.5px] max-w-md">
                      {(r.descriptions || []).length === 0 ? <span className="text-muted-foreground">—</span> :
                        (r.descriptions || []).map((d, i) => (
                          <div key={`${r.id}-desc-${i}`} className="truncate">{d}</div>
                        ))}
                      {Array.isArray(r.supporting_tenders) && r.supporting_tenders.length > 0 && (
                        <div className="mt-1 text-[11px] text-muted-foreground" title={r.supporting_tenders.join("\n")}>
                          <span className="uppercase tracking-wider text-[9.5px] mr-1 text-primary/70">Supp. Co:</span>
                          {r.supporting_tenders.join(", ")}
                        </div>
                      )}
                    </td>
                    <td className={`font-mono text-[12px] ${tab === "pending" && isOverdue(r.deadline) ? "text-destructive font-semibold" : ""}`}>
                      {fmtDeadline(r.deadline)}
                      {(r.postpone_history || []).length > 0 && (() => {
                        const first = r.postpone_history[0] || {};
                        const original = first.old_deadline;
                        return (
                          <div className="text-[10px] text-muted-foreground normal-case tracking-normal leading-tight mt-0.5">
                            <div>Postponed {r.postpone_history.length}× · latest → <b className="text-amber-700">{fmtDeadline(r.deadline)}</b></div>
                            {original && (
                              <div>Originally: <span className="line-through">{fmtDeadline(original)}</span></div>
                            )}
                          </div>
                        );
                      })()}
                    </td>
                    <td className="num" style={{ textAlign: "right" }}>
                      {tab === "pending" ? (
                        <div className="flex gap-1 justify-end items-center flex-wrap">
                          <Button variant="ghost" size="icon" className="h-8 w-8"
                            style={{ color: r.customer_phone ? "#128C7E" : "#94a3b8" }}
                            title={r.customer_phone ? `WhatsApp reminder → ${r.customer_phone}` : "No mobile on file — click Edit to add one"}
                            onClick={() => sendWhatsAppReminder(r)}
                            data-testid={`pw-whatsapp-${r.id}`}>
                            <WhatsappLogo size={15} weight="fill" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8"
                            style={{ color: r.customer_email ? undefined : "#94a3b8" }}
                            title={r.customer_email ? `Email reminder → ${r.customer_email}` : "No email on file — click Edit to add one"}
                            onClick={() => sendEmailReminder(r)}
                            data-testid={`pw-email-${r.id}`}>
                            <EnvelopeSimple size={14} />
                          </Button>
                          <Button size="sm" variant="outline" className="h-7 px-2 text-[11px] ml-1"
                            onClick={() => setStatus(r, "completed")} data-testid={`pw-done-${r.id}`}>
                            <Check size={12} className="mr-1" /> Done
                          </Button>
                          <Button size="sm" variant="outline" className="h-7 px-2 text-[11px]"
                            onClick={() => setPostponing(r)} data-testid={`pw-postpone-${r.id}`}>
                            <Clock size={12} className="mr-1" /> Postpone
                          </Button>
                          <Button size="sm" variant="outline" className="h-7 px-2 text-[11px]"
                            onClick={() => { setEditing(r); setOpen(true); }} data-testid={`pw-edit-${r.id}`}>
                            <PencilSimple size={12} className="mr-1" /> Edit
                          </Button>
                          <Button size="sm" variant="outline" className="h-7 px-2 text-[11px] text-destructive"
                            onClick={() => setStatus(r, "cancelled")} data-testid={`pw-cancel-${r.id}`}>
                            <XCircle size={12} className="mr-1" /> Cancel
                          </Button>
                        </div>
                      ) : (
                        <div className="flex gap-1 justify-end">
                          <Button size="sm" variant="outline" className="h-7 px-2 text-[11px]"
                            onClick={() => { setEditing(r); setOpen(true); }} data-testid={`pw-edit-${r.id}`}>
                            <PencilSimple size={12} className="mr-1" /> Edit
                          </Button>
                          <Button size="sm" variant="outline" className="h-7 px-2 text-[11px]"
                            onClick={() => setStatus(r, "pending")}>Reopen</Button>
                          {can("pending_works", "delete") && (
                            <Button size="sm" variant="ghost" className="h-7 w-7 text-destructive p-0"
                              onClick={() => del(r.id)}>
                              <Trash size={13} />
                            </Button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={!!postponing} onOpenChange={(v) => { if (!v) setPostponing(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Clock size={18} /> Postpone Work
            </DialogTitle>
          </DialogHeader>
          {postponing && (
            <PostponeDialog row={postponing} onClose={() => setPostponing(null)} onDone={load} />
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={statementEmailOpen} onOpenChange={setStatementEmailOpen}>
        <DialogContent className="max-w-md" data-testid="pw-email-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <EnvelopeSimple size={18} /> Email Daily Pending Works ({tab})
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label className="eyebrow">Recipients (optional override)</Label>
            <Textarea
              rows={3}
              value={statementEmailTo}
              onChange={(e) => setStatementEmailTo(e.target.value)}
              placeholder="owner@example.com, accountant@example.com"
              data-testid="pw-email-recipients"
            />
            <p className="text-[11px] text-muted-foreground">
              Leave blank to send to the configured recipients in Integrations → Daily Close Report Recipients.
              The email includes all {tab} tasks with parties, items, deadlines and amounts.
            </p>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setStatementEmailOpen(false)} disabled={statementEmailSending} data-testid="pw-email-cancel">Cancel</Button>
            <Button onClick={sendStatementEmail} disabled={statementEmailSending} data-testid="pw-email-send">
              <EnvelopeSimple size={13} className="mr-1.5" />
              {statementEmailSending ? "Sending…" : "Send Email"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
